import {
  bindCollection,
  bindDocument,
  walkSet,
  firestoreOptions,
  FirestoreOptions,
  OperationsType,
} from '../vuefire-core/src'
import firebase from 'firebase/app'
import Vue, { PluginFunction } from 'vue'

const ops: OperationsType = {
  set: (target, key, value) => walkSet(target, key, value),
  add: (array, index, data) => array.splice(index, 0, data),
  remove: (array, index) => array.splice(index, 1),
}

function bind(
  vm: Record<string, any>,
  key: string,
  ref:
    | firebase.firestore.CollectionReference
    | firebase.firestore.Query
    | firebase.firestore.DocumentReference,
  ops: OperationsType,
  options: FirestoreOptions
) {
  return new Promise((resolve, reject) => {
    let unbind
    if ('where' in ref) {
      unbind = bindCollection(
        {
          vm,
          key,
          ops,
          collection: ref,
          resolve,
          reject,
        },
        options
      )
    } else {
      unbind = bindDocument(
        {
          vm,
          key,
          ops,
          document: ref,
          resolve,
          reject,
        },
        options
      )
    }
    vm._firestoreUnbinds[key] = unbind
  })
}

interface PluginOptions {
  bindName?: string
  unbindName?: string
  serialize?: FirestoreOptions['serialize']
  reset?: FirestoreOptions['reset']
  wait?: FirestoreOptions['wait']
}

const defaultOptions: Readonly<Required<PluginOptions>> = {
  bindName: '$bind',
  unbindName: '$unbind',
  serialize: firestoreOptions.serialize,
  reset: firestoreOptions.reset,
  wait: firestoreOptions.wait,
}

declare module 'vue/types/vue' {
  // TODO: export types to allow custom function names
  interface Vue {
    $bind(
      name: string,
      reference: firebase.firestore.Query | firebase.firestore.CollectionReference,
      options?: FirestoreOptions
    ): Promise<firebase.firestore.DocumentData[]>
    $bind(
      name: string,
      reference: firebase.firestore.DocumentReference,
      options?: FirestoreOptions
    ): Promise<firebase.firestore.DocumentData>
    $unbind: (name: string, reset?: FirestoreOptions['reset']) => void
    $firestoreRefs: Readonly<
      Record<string, firebase.firestore.DocumentReference | firebase.firestore.CollectionReference>
    >
    // _firestoreSources: Readonly<
    //   Record<string, firebase.firestore.CollectionReference | firebase.firestore.Query | firebase.firestore.DocumentReference>
    // >
    _firestoreUnbinds: Readonly<
      Record<string, ReturnType<typeof bindCollection | typeof bindDocument>>
    >
  }
}

type VueFirestoreObject = Record<
  string,
  | firebase.firestore.DocumentReference
  | firebase.firestore.Query
  | firebase.firestore.CollectionReference
>
type FirestoreOption<V> = VueFirestoreObject | ((this: V) => VueFirestoreObject)

declare module 'vue/types/options' {
  interface ComponentOptions<V extends Vue> {
    firestore?: FirestoreOption<V>
  }
}

export const firestorePlugin: PluginFunction<PluginOptions> = function firestorePlugin(
  Vue,
  pluginOptions = defaultOptions
) {
  const strategies = Vue.config.optionMergeStrategies
  strategies.firestore = strategies.provide

  const globalOptions = Object.assign({}, defaultOptions, pluginOptions)
  const { bindName, unbindName } = globalOptions

  Vue.prototype[unbindName] = function firestoreUnbind(
    key: string,
    reset?: FirestoreOptions['reset']
  ) {
    if (this._firestoreUnbinds[key]) this._firestoreUnbinds[key](reset)
    delete this._firestoreUnbinds[key]
    delete this.$firestoreRefs[key]
  }

  Vue.prototype[bindName] = function firestoreBind(
    this: Vue,
    key: string,
    ref:
      | firebase.firestore.Query
      | firebase.firestore.CollectionReference
      | firebase.firestore.DocumentReference,
    userOptions?: FirestoreOptions
  ) {
    const options = Object.assign({}, globalOptions, userOptions)

    if (this._firestoreUnbinds[key]) {
      this[unbindName as keyof Vue](
        key,
        // if wait, allow overriding with a function or reset, otherwise, force reset to false
        // else pass the reset option
        options.wait ? (typeof options.reset === 'function' ? options.reset : false) : options.reset
      )
    }
    const promise = bind(this, key, ref, ops, options)
    // @ts-ignore we are allowed to write it
    this.$firestoreRefs[key] = ref
    return promise
  }

  Vue.mixin({
    beforeCreate(this: Vue) {
      this._firestoreUnbinds = Object.create(null)
      this.$firestoreRefs = Object.create(null)
    },
    created(this: Vue) {
      const { firestore } = this.$options
      const refs = typeof firestore === 'function' ? firestore.call(this) : firestore
      if (!refs) return
      for (const key in refs) {
        // @ts-ignore
        this[bindName as keyof Vue](key, refs[key], globalOptions)
      }
    },

    beforeDestroy(this: Vue) {
      for (const subKey in this._firestoreUnbinds) {
        if (this._firestoreUnbinds[subKey]) this._firestoreUnbinds[subKey]()
      }
      // @ts-ignore we are allowed to write it
      this._firestoreUnbinds = null
      // @ts-ignore we are allowed to write it
      this.$firestoreRefs = null
    },
  })
}
